import { useEffect, useState } from 'react';
import { AlarmClock, X } from 'lucide-react';
import { Button } from '@hrms/ui';
import { useNotificationSocket, type RealtimeNotification } from '../../lib/socket.js';
import { tsr } from '../../lib/api.js';
import { idempotentRequest } from '../../lib/idempotency.js';
import { errorMessage } from './errorMessage.js';

const REMINDER_KEYS = new Set(['project.task_reminder_morning', 'project.task_reminder_evening']);
/** Single-task reminders. Popup-eligible only when the realtime payload carries `data.taskId` (the
 * server forwards it from the notification payload — `packages/notify` `deliverOne`): a reminder
 * with no task to snooze against stays a bell-only notification, exactly as before. */
const SINGLE_TASK_KEYS = new Set(['project.task_due', 'project.task_overdue_escalation']);
const SNOOZE_MINUTES = 30;
const MAX_VISIBLE_TOASTS = 3;

interface ReminderToast {
  /** The notification's own id — stable, so re-renders don't reshuffle the stack. */
  id: string;
  title: string;
  body?: string;
  /** Only the snooze-flush path (one real task) carries this; the morning/evening digest (N tasks)
   * has no single task to snooze against — see `packages/notify/src/realtime.ts`'s doc comment on
   * `RealtimeNotification.data`. Dismiss-only when absent. */
  taskId?: string;
}

/**
 * PM gap P5, "remind me later" popup — the one behavior wired to `snoozeTaskReminder` in this
 * whole codebase; every other caller of that route was dead until this component. Listens on the
 * EXISTING realtime notification channel (`useNotificationSocket`, same context `NotificationBell`
 * already reads — see `lib/socket.tsx`) rather than opening a second socket listener.
 *
 * Mounted once, next to `NotificationBell`, inside `AppShell`'s `SocketProvider` — see AppShell.tsx.
 *
 * Every `project.task_reminder_morning`/`_evening` notification is popup-eligible: the org only gets
 * this key at all when its admin turned that reminder slot on (`TaskReminderScheduleAdminPage.tsx`),
 * so there is nothing further to gate on client-side (the schedule's own `popup` flag is a
 * schedule-time decision that never reaches the browser as a field — it only decided server-side
 * whether to send the notification at all).
 */
export function TaskReminderPopup() {
  const socket = useNotificationSocket();
  const [toasts, setToasts] = useState<ReminderToast[]>([]);

  useEffect(() => {
    return socket.onNotification((note: RealtimeNotification) => {
      const taskId = typeof note.data?.taskId === 'string' ? note.data.taskId : undefined;
      if (!REMINDER_KEYS.has(note.key) && !(SINGLE_TASK_KEYS.has(note.key) && taskId)) return;
      setToasts((prev) => [...prev, { id: note.id, title: note.title, body: note.body, taskId }]);
    });
  }, [socket]);

  function dismiss(id: string) {
    // Dismissing must NOT create a snooze row — the task simply reappears at the next scheduled
    // slot, which is already the server's behavior on its own. This just removes the toast locally.
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }

  if (toasts.length === 0) return null;

  return (
    // Phone: full width minus gutters and lifted above the copilot FAB (bottom-5 right-5). From sm up it
    // is a right-anchored max-w-sm stack; from lg the FAB no longer collides, so it drops back down.
    <div className="fixed inset-x-4 bottom-20 z-40 flex flex-col gap-2 sm:inset-x-auto sm:right-6 sm:w-full sm:max-w-sm lg:bottom-4">
      {/* Only the latest 3 are shown; older ones are still in the notification bell. */}
      {toasts.slice(-MAX_VISIBLE_TOASTS).map((toast) => (
        <ReminderToastCard key={toast.id} toast={toast} onDismiss={() => dismiss(toast.id)} />
      ))}
    </div>
  );
}

function ReminderToastCard({
  toast,
  onDismiss,
}: {
  toast: ReminderToast;
  onDismiss: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const snooze = tsr.projects.snoozeTaskReminder.useMutation();

  function handleSnooze() {
    if (!toast.taskId) return;
    setError(null);
    snooze.mutate(
      {
        params: { taskId: toast.taskId },
        body: { minutes: SNOOZE_MINUTES },
        ...idempotentRequest(),
      },
      {
        onSuccess: onDismiss,
        onError: (err) => setError(errorMessage(err)),
      },
    );
  }

  return (
    <div
      role="status"
      className="flex flex-col gap-2 rounded-xl border border-border bg-surface p-3.5 shadow-lg"
    >
      <div className="flex items-start gap-2.5">
        <AlarmClock className="mt-0.5 size-4 shrink-0 text-accent" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-fg">{toast.title}</p>
          {toast.body && (
            <p className="mt-0.5 whitespace-pre-line text-xs text-fg-muted">{toast.body}</p>
          )}
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="-m-1.5 shrink-0 p-1.5 text-fg-muted hover:text-fg"
        >
          <X className="size-4" />
        </button>
      </div>

      {error && (
        <p role="alert" className="rounded-md bg-danger-subtle px-2.5 py-1.5 text-xs text-danger">
          {error}
        </p>
      )}

      {toast.taskId && (
        <div className="flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={handleSnooze} loading={snooze.isPending}>
            Remind me in {SNOOZE_MINUTES} min
          </Button>
        </div>
      )}
    </div>
  );
}
